'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { limitNumberOfPaginationPages } from '@//utils/pagination';

const buildHref = (rootUrl: string, currentParams: URLSearchParams, query?: string) => {
  const next = new URLSearchParams();
  // Preserve sort from current URL
  const sort = currentParams.get('sort');
  if (sort) next.set('sort', sort);

  // Set offset from the API-provided pagination query
  if (query) {
    const source = new URLSearchParams(query);
    const offset = source.get('offset');
    if (offset && offset !== '0') {
      next.set('offset', offset);
    }
  }

  const queryString = next.toString();
  return `${rootUrl}${queryString ? `?${queryString}` : ''}`;
};

interface PaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const Pagination = ({ rootUrl, pagination }: PaginationProps) => {
  const t = useTranslations('other.pagination');
  const router = useRouter();
  const currentParams = useSearchParams();

  if (!pagination || pagination.pages.length <= 1) {
    return null;
  }

  const navigate = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(href, { scroll: false });
    (e.target as HTMLElement).blur();
    requestAnimationFrame(() => {
      setTimeout(() => {
        const heading = document.getElementById('campaign-vehicles-heading');
        const grid = document.getElementById('campaign-vehicles');
        const target = heading ?? grid;
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        grid?.focus({ preventScroll: true });
      }, 300);
    });
  };

  const prev = pagination.previousPage?.query
    ? buildHref(rootUrl, currentParams, pagination.previousPage.query)
    : undefined;
  const next = pagination.nextPage?.query
    ? buildHref(rootUrl, currentParams, pagination.nextPage.query)
    : undefined;

  const pages = limitNumberOfPaginationPages(pagination.pages);

  return (
    <StyledPagination testId="campaign-page-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink
        variant="prev"
        href={prev}
        testId="campaign-page-pagination-prev"
        onClick={prev ? (e) => navigate(e, prev) : undefined}
      />
      {pages.map((page) => {
        const href = buildHref(rootUrl, currentParams, page.query);
        return (
          <PaginationLink
            key={page.displayName}
            href={href}
            onClick={(e) => navigate(e, href)}
            title={t('goTo.title', { page: page.displayName })}
            testId={`campaign-page-pagination-page-${page.displayName}`}
            isActive={page.selected}
          >
            {page.displayName}
          </PaginationLink>
        );
      })}
      <PaginationNavLink
        variant="next"
        href={next}
        testId="campaign-page-pagination-next"
        onClick={next ? (e) => navigate(e, next) : undefined}
      />
    </StyledPagination>
  );
};

export default Pagination;
